/**
 * Backend Health Check Utility
 *
 * This module provides utilities to detect backend availability for offline mode support.
 * It implements caching to avoid excessive health checks and graceful error handling.
 *
 * @module backendHealth
 */

import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Backend health check configuration
 */
const HEALTH_CHECK_CONFIG = {
  /** Health check endpoint path */
  endpoint: '/api/health',

  /** Timeout in milliseconds (5 seconds) */
  timeout: 5000,

  /** Cache duration in milliseconds (30 seconds) */
  cacheDuration: 30000,

  /** Number of retry attempts */
  retries: 1,

  /** Delay between retries in milliseconds */
  retryDelay: 1000,
};

/**
 * Health check result cache
 */
interface HealthCheckCache {
  isAvailable: boolean | null;
  timestamp: number;
}

/**
 * In-memory cache for health check results
 * Avoids excessive API calls while keeping data fresh
 */
let healthCheckCache: HealthCheckCache | null = null;

/**
 * Perform a backend health check
 *
 * This function pings the backend's health endpoint to determine if it's available.
 * Results are cached to avoid excessive API calls.
 *
 * @param {string} apiBaseUrl - The base URL of the backend API
 * @param {boolean} bypassCache - Force a new health check, ignoring cache
 * @returns {Promise<boolean>} True if backend is available, false otherwise
 *
 * @example
 * ```typescript
 * // Basic usage
 * const isHealthy = await checkBackendHealth('http://localhost:3000');
 *
 * // Force bypass cache
 * const isHealthy = await checkBackendHealth('http://localhost:3000', true);
 * ```
 */
export async function checkBackendHealth(
  apiBaseUrl: string = '',
  bypassCache: boolean = false
): Promise<boolean> {
  const now = Date.now();

  // Check cache first (unless bypassing)
  if (!bypassCache && healthCheckCache) {
    const cacheAge = now - healthCheckCache.timestamp;
    if (cacheAge < HEALTH_CHECK_CONFIG.cacheDuration) {
      console.debug('[BackendHealth] Using cached health check result:', healthCheckCache.isAvailable);
      return healthCheckCache.isAvailable === true;
    }
  }

  console.debug('[BackendHealth] Performing backend health check...');

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_CONFIG.timeout);

    const response = await fetch(`${apiBaseUrl}${HEALTH_CHECK_CONFIG.endpoint}`, {
      method: 'GET',
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      // Backend is healthy
      healthCheckCache = {
        isAvailable: true,
        timestamp: now,
      };
      console.debug('[BackendHealth] Backend is healthy');
      return true;
    } else {
      // Backend returned error status
      healthCheckCache = {
        isAvailable: false,
        timestamp: now,
      };
      console.warn('[BackendHealth] Backend returned error status:', response.status);
      return false;
    }
  } catch (error) {
    // Backend is unavailable (network error, timeout, etc.)
    healthCheckCache = {
      isAvailable: false,
      timestamp: now,
    };

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        console.warn('[BackendHealth] Backend health check timed out');
      } else {
        console.warn('[BackendHealth] Backend health check failed:', error.message);
      }
    } else {
      console.warn('[BackendHealth] Backend health check failed:', error);
    }

    return false;
  }
}

/**
 * React hook for backend availability state
 *
 * This hook provides a reactive way to check and monitor backend availability.
 * It automatically performs health checks on mount and provides a refetch function.
 *
 * @param {string} apiBaseUrl - The base URL of the backend API
 * @param {object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable health checks (default: true)
 * @param {number} options.checkInterval - Interval in ms to recheck (0 = disable, default: 0)
 * @returns {object} Backend availability state and control functions
 *
 * @example
 * ```typescript
 * function MyComponent() {
 *   const { isAvailable, isChecking, error, refetch } = useBackendHealth('http://localhost:3000');
 *
 *   if (isChecking) return <div>Checking backend...</div>;
 *   if (!isAvailable) return <div>Backend is offline</div>;
 *
 *   return <div>Backend is online</div>;
 * }
 * ```
 */
export function useBackendHealth(
  apiBaseUrl: string = '',
  options: {
    enabled?: boolean;
    checkInterval?: number;
  } = {}
) {
  const { enabled = true, checkInterval = 0 } = options;

  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Use ref to avoid stale closures in setInterval
  const isCheckingRef = useRef(isChecking);
  isCheckingRef.current = isChecking;

  /**
   * Perform health check and update state
   */
  const performHealthCheck = useCallback(async () => {
    if (!enabled) {
      console.debug('[BackendHealth] Health check disabled, skipping');
      return;
    }

    // Prevent concurrent checks
    if (isCheckingRef.current) {
      console.debug('[BackendHealth] Health check already in progress, skipping');
      return;
    }

    setIsChecking(true);
    setError(null);

    try {
      const result = await checkBackendHealth(apiBaseUrl);
      setIsAvailable(result);
      setError(null);
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error('Unknown error');
      setError(errorObj);
      setIsAvailable(false);
    } finally {
      setIsChecking(false);
    }
  }, [apiBaseUrl, enabled]);

  /**
   * Refetch health check (bypasses cache)
   */
  const refetch = useCallback(async () => {
    console.debug('[BackendHealth] Manual refetch requested');
    await checkBackendHealth(apiBaseUrl, true); // Bypass cache
    await performHealthCheck();
  }, [apiBaseUrl, performHealthCheck]);

  /**
   * Clear health check cache
   */
  const clearCache = useCallback(() => {
    healthCheckCache = null;
    console.debug('[BackendHealth] Cache cleared');
  }, []);

  // Initial health check on mount
  useEffect(() => {
    if (!enabled) {
      setIsAvailable(null);
      setIsChecking(false);
      return;
    }

    performHealthCheck();

    // Set up interval for periodic checks if configured
    if (checkInterval > 0) {
      const intervalId = setInterval(() => {
        console.debug('[BackendHealth] Periodic health check');
        performHealthCheck();
      }, checkInterval);

      return () => {
        clearInterval(intervalId);
      };
    }

    // Return empty cleanup function for consistency
    return undefined;
  }, [enabled, checkInterval, performHealthCheck]);

  return {
    /** Backend availability status (null = unknown, true = available, false = unavailable) */
    isAvailable,

    /** Whether a health check is currently in progress */
    isChecking,

    /** Error from last health check (if any) */
    error,

    /** Manually trigger a new health check (bypasses cache) */
    refetch,

    /** Clear the health check cache */
    clearCache,
  };
}

/**
 * Check if backend is available synchronously (from cache)
 *
 * This function returns the cached health check result without making a new API call.
 * Useful for quick checks that don't need fresh data.
 *
 * @returns {boolean | null} Cached availability status (null = no cache)
 *
 * @example
 * ```typescript
 * const cachedStatus = getBackendHealthFromCache();
 * if (cachedStatus === true) {
 *   // Backend is healthy (from cache)
 * } else if (cachedStatus === false) {
 *   // Backend is unhealthy (from cache)
 * } else {
 *   // No cache available, need to perform health check
 * }
 * ```
 */
export function getBackendHealthFromCache(): boolean | null {
  if (!healthCheckCache) {
    return null;
  }

  const now = Date.now();
  const cacheAge = now - healthCheckCache.timestamp;

  // Return null if cache is expired
  if (cacheAge > HEALTH_CHECK_CONFIG.cacheDuration) {
    return null;
  }

  return healthCheckCache.isAvailable;
}

/**
 * Clear the health check cache
 *
 * Forces the next health check to fetch fresh data from the backend.
 *
 * @example
 * ```typescript
 * // User clicked "Retry" button
 * clearBackendHealthCache();
 * await checkBackendHealth(apiBaseUrl);
 * ```
 */
export function clearBackendHealthCache(): void {
  healthCheckCache = null;
  console.debug('[BackendHealth] Cache cleared');
}
